import { StateValues } from './enums/state.enum';
import { StationType, StationTypeValues } from './enums/stationType.enum';

/** GeoJSON Point shape used across installations and stations. */
export type GeoPoint = { type: 'Point'; coordinates: [number, number] };

export const DEFAULT_GEO_POINT: GeoPoint = { type: 'Point', coordinates: [0, 0] };

/**
 * Coerces an unknown value to a finite number.
 * Accepts both actual numbers and numeric strings.
 * Returns `undefined` for NaN, Infinity, null, objects, etc.
 */
export function asNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number.parseFloat(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
}

/**
 * Coerces an unknown value to a `StateValues` integer.
 * Accepts numbers and numeric strings. Falls back to `fallback` otherwise.
 */
export function asState(value: unknown, fallback: StateValues): StateValues {
  if (typeof value === 'number' && Number.isFinite(value)) return value as StateValues;
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number.parseInt(value, 10);
    if (!Number.isNaN(parsed)) return parsed as StateValues;
  }
  return fallback;
}

/**
 * Coerces an unknown value to a `StationTypeValues` string.
 * Returns `fallback` when the value is missing or not a recognized StationType.
 */
export function asStationType(value: unknown, fallback: StationTypeValues): StationTypeValues {
  if (typeof value === 'string' && value.trim().length > 0) {
    const normalized = value.trim();
    if ((Object.values(StationType) as string[]).includes(normalized)) {
      return normalized as StationTypeValues;
    }
  }
  return fallback;
}

/**
 * Parses an unknown value into a validated `GeoPoint`.
 * Accepts any object with `{ type: 'Point', coordinates: [number, number] }` shape.
 * Returns `undefined` if coordinates are missing or non-numeric.
 */
export function asGeoPoint(value: unknown): GeoPoint | undefined {
  if (typeof value !== 'object' || value === null) return undefined;
  const obj = value as Record<string, unknown>;
  const coords = Array.isArray(obj.coordinates) ? obj.coordinates : [];
  if (obj.type !== 'Point' || coords.length < 2) return undefined;
  const lng = asNumber(coords[0]);
  const lat = asNumber(coords[1]);
  if (lng === undefined || lat === undefined) return undefined;
  return { type: 'Point', coordinates: [lng, lat] };
}
